import { UserProfile } from './types';
import { whitelineApi } from '@/shared/api';

export type {
  UserProfile
} from './types';

interface GetClientMchdRequest {
  orgId: string;
}

export interface CreateClientWarehouseRequest {
  acceptance_time?: string;
  address: string;
  address_fias_id: string;
  contact_person: string;
  driver_comment?: string;
  name: string;
  orgId: string;
  phone: string;
}

export interface CreateClientWarehouseResponse {
  id: string;
}

export const createClientWarehouse = async (
  payload: CreateClientWarehouseRequest
): Promise<CreateClientWarehouseResponse> => {
  const response = await whitelineApi.post<CreateClientWarehouseResponse>(`/api/clients/${payload.orgId}/warehouses`, {
    acceptance_time: payload.acceptance_time ?? '',
    address: payload.address,
    address_fias_id: payload.address_fias_id,
    contact_person: payload.contact_person,
    driver_comment: payload.driver_comment ?? '',
    name: payload.name,
    phone: payload.phone
  });

  return response.data;
};

export interface DeleteClientWarehouseByIdRequest {
  warehouseId: string;
}

export interface DeleteClientWarehouseByIdResponse {
  id?: string;
}

export const deleteClientWarehouseById = async (
  payload: DeleteClientWarehouseByIdRequest
): Promise<DeleteClientWarehouseByIdResponse> => {
  const response = await whitelineApi.delete<DeleteClientWarehouseByIdResponse>(
    `/api/warehouses/${payload.warehouseId}`
  );

  return response.data;
};

export enum MchdStatus {
  Draft = 'DRAFT',
  Created = 'CREATED',
  Active = 'ACTIVE',
  Revoked = 'REVOKED',
  Expired = 'EXPIRED'
}

export interface GetClientMchdItem {
  attorney_name: string | null;
  end_date: string;
  id: string;
  start_date: string;
  status: MchdStatus | null;
}

export interface GetClientMchdPendingInvite {
  expires_at: string;
  sent_at: string;
}

export interface GetClientMchdResponse {
  items: GetClientMchdItem[] | null;
  pending_invite?: GetClientMchdPendingInvite | null;
  total_count: number;
}

export const getClientMchd = async (payload: GetClientMchdRequest): Promise<GetClientMchdResponse> => {
  const response = await whitelineApi.get<GetClientMchdResponse>(`/api/clients/${payload.orgId}/mchd`);

  return response.data;
};

interface GetClientWarehousesRequest {
  orgId: string;
}

export interface GetClientWarehousesItem {
  acceptance_time?: string | null;
  address: string | null;
  city?: string | null;
  contact_person?: string | null;
  driver_comment?: string | null;
  id: string;
  name: string | null;
  phone?: string | null;
}

export type GetClientWarehousesResponse = GetClientWarehousesItem[] | {
  items: GetClientWarehousesItem[];
  total_count?: number;
};

export const getClientWarehouses = async (
  payload: GetClientWarehousesRequest
): Promise<GetClientWarehousesResponse> => {
  const response = await whitelineApi.get<GetClientWarehousesResponse>(`/api/clients/${payload.orgId}/warehouses`);

  return response.data;
};

export interface UpdateClientWarehouseByIdRequest {
  acceptance_time?: string;
  contact_person: string;
  driver_comment?: string;
  name: string;
  phone: string;
  warehouseId: string;
}

export interface UpdateClientWarehouseByIdResponse {
  id: string;
}

export const updateClientWarehouseById = async (
  payload: UpdateClientWarehouseByIdRequest
): Promise<UpdateClientWarehouseByIdResponse> => {
  const response = await whitelineApi.patch<UpdateClientWarehouseByIdResponse>(
    `/api/warehouses/${payload.warehouseId}`,
    {
      acceptance_time: payload.acceptance_time ?? '',
      contact_person: payload.contact_person,
      driver_comment: payload.driver_comment ?? '',
      name: payload.name,
      phone: payload.phone
    }
  );

  return response.data;
};

interface GetUserByIdRequest {
  id: string;
}

type GetUserByIdResponse = UserProfile;

export const getUserById = async (payload: GetUserByIdRequest): Promise<GetUserByIdResponse> => {
  const response = await whitelineApi.get<GetUserByIdResponse>(`/api/user/${payload.id}`);

  return response.data;
};

export interface SendMchdInviteRequest {
  email: string | null;
  orgId: string;
}

export interface SendMchdInviteResponse {
  email: string | null;
  expires_at: string;
  queued: boolean;
  sent: boolean;
}

export const sendMchdInvite = async (payload: SendMchdInviteRequest): Promise<SendMchdInviteResponse> => {
  const response = await whitelineApi.post<SendMchdInviteResponse>(
    `/api/clients/${payload.orgId}/mchd/invite`,
    {
      email: payload.email
    }
  );

  return response.data;
};

export interface SendEmailVerificationResponse {
  email: string | null;
  expires_at: string;
  sent: boolean;
}

export const sendEmailVerification = async (): Promise<SendEmailVerificationResponse> => {
  const response = await whitelineApi.post<SendEmailVerificationResponse>('/api/email/send');

  return response.data;
};

export interface UpdateUserByIdRequest {
  current_password?: string;
  email: string;
  first_name?: string;
  id: string;
  last_name?: string;
  middle_name?: string;
  password?: string;
  phone?: string;
}

interface UpdateUserByIdBody {
  current_password?: string;
  email: string;
  first_name?: string;
  last_name?: string;
  middle_name?: string;
  password?: string;
  phone?: string;
}

type UpdateUserByIdResponse = UserProfile;

export const updateUserById = async (payload: UpdateUserByIdRequest): Promise<UpdateUserByIdResponse> => {
  const body: UpdateUserByIdBody = {
    ...(payload.password ? { current_password: payload.current_password ?? '' } : {}),
    email: payload.email,
    ...(payload.first_name !== undefined ? { first_name: payload.first_name } : {}),
    ...(payload.last_name !== undefined ? { last_name: payload.last_name } : {}),
    ...(payload.middle_name !== undefined ? { middle_name: payload.middle_name } : {}),
    ...(payload.password ? { password: payload.password } : {}),
    ...(payload.phone !== undefined ? { phone: payload.phone } : {})
  };

  const response = await whitelineApi.patch<UpdateUserByIdResponse>(`/api/user/${payload.id}`, body);

  return response.data;
};